/**
 * localStorage bridge for theme persistence.
 *
 * All access is wrapped in `try/catch` and guarded against missing `window`
 * so the module is safe under SSR, vitest's Node environment, Safari private
 * mode, Safari ITP, and similar contexts where `localStorage` may throw.
 *
 * The persisted value is a single `Theme.slug` (lowercase ASCII kebab).
 */

export const THEME_STORAGE_KEY = 'axi.theme' as const;

function getStorage(): Storage | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

export function readStoredThemeSlug(): string | null {
  const storage = getStorage();
  if (!storage) return null;
  try {
    return storage.getItem(THEME_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function writeStoredThemeSlug(slug: string): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.setItem(THEME_STORAGE_KEY, slug);
  } catch {
    // Quota exceeded, private mode, security error — silently no-op.
  }
}

export function clearStoredThemeSlug(): void {
  const storage = getStorage();
  if (!storage) return;
  try {
    storage.removeItem(THEME_STORAGE_KEY);
  } catch {
    // Silent no-op.
  }
}